// SigmaStudioSIPOC.jsx
import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
} from "reactflow";
import "reactflow/dist/style.css";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import "./SigmaStudio.css";

const SigmaStudioSIPOC = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showWhyUsed, setShowWhyUsed] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ─── Core SIPOC ───────────────────────────────────────
  const [processName, setProcessName] = useState("");
  const [taskName, setTaskName] = useState("");
  const [taskDetails, setTaskDetails] = useState("");
  const [finalRemarks, setFinalRemarks] = useState("");

  const [suppliers, setSuppliers] = useState([]);
  const [inputs, setInputs] = useState([]);
  const [processSteps, setProcessSteps] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [customers, setCustomers] = useState([]);

  // ─── Advanced sections ────────────────────────────────
  const [metrics, setMetrics] = useState([]);
  const [timeInfo, setTimeInfo] = useState([]);
  const [responsible, setResponsible] = useState([]);
  const [resources, setResources] = useState([]);
  const [risks, setRisks] = useState([]);
  const [custRequirements, setCustRequirements] = useState([]);

  // New item inputs
  const [newSupplier, setNewSupplier] = useState("");
  const [newInput, setNewInput] = useState("");
  const [newProcessStep, setNewProcessStep] = useState("");
  const [newOutput, setNewOutput] = useState("");
  const [newCustomer, setNewCustomer] = useState("");
  const [newMetric, setNewMetric] = useState("");
  const [newTime, setNewTime] = useState("");
  const [newResp, setNewResp] = useState("");
  const [newResource, setNewResource] = useState("");
  const [newRisk, setNewRisk] = useState("");
  const [newReq, setNewReq] = useState("");

  // UI toggles
  const [showTDetails, setShowTDetails] = useState(false);
  const [showBasicChart, setShowBasicChart] = useState(false);
  const [showDetailedChart, setShowDetailedChart] = useState(false);

  const basicRef = useRef(null);
  const detailedRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const handleDragEnd = (sectionSetter) => (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      sectionSetter((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addItem = (setter, list, value, clearFn) => {
    if (!value.trim()) return;
    setter([...list, { id: Date.now(), text: value.trim() }]);
    clearFn("");
  };

  // const updateItem = (setter, list, id, newText) => {
  //   setter(
  //     list.map((item) =>
  //       item.id === id ? { ...item, text: newText.trim() } : item
  //     )
  //   );
  // };

  // const removeItem = (setter, list, id) => {
  //   setter(list.filter((item) => item.id !== id));
  // };

  const populateExampleData = () => {
    setProcessName("Flexographic Printing – Self Adhesive Label Production");
    setTaskName("Label Print & Die-Cut to Final Packing");
    setTaskDetails(
      "End-to-end process from raw material receipt to packed labeled rolls dispatch."
    );
    setFinalRemarks(
      "High-speed, multi-variable process. Key variation sources: substrate tension, ink viscosity, die pressure."
    );

    setSuppliers([
      { id: 1, text: "Substrate – Avery Dennison / UPM Raflatac" },
      { id: 2, text: "Ink & Varnish – Toyo Ink / Siegwerk" },
      { id: 3, text: "Release Liner Supplier" },
      { id: 4, text: "Rotary Die Supplier" },
    ]);

    setInputs([
      { id: 1, text: "Pressure Sensitive Laminate" },
      { id: 2, text: "CMYK + Spot / Varnish Inks" },
      { id: 3, text: "Printing Plates & Anilox Rolls" },
      { id: 4, text: "UV Lamp Electricity" },
    ]);

    setProcessSteps([
      { id: 1, text: "Unwind & Web Guiding" },
      { id: 2, text: "Multi-color Flexo Printing" },
      { id: 3, text: "Varnish / Cold Foil Application" },
      { id: 4, text: "Die Cutting & Matrix Stripping" },
      { id: 5, text: "Slitting & Rewinding" },
      { id: 6, text: "100% Vision Inspection & Packing" },
    ]);

    setOutputs([
      { id: 1, text: "Finished Label Rolls" },
      { id: 2, text: "Waste Matrix & Trim Scrap" },
      { id: 3, text: "Quality Inspection Report" },
    ]);

    setCustomers([
      { id: 1, text: "FMCG Brand Owners" },
      { id: 2, text: "Pharma & Nutraceutical Companies" },
      { id: 3, text: "Contract Packaging Firms" },
    ]);

    // Advanced example
    setMetrics([
      { id: 1, text: "DPMO < 500" },
      { id: 2, text: "Cpk ≥ 1.33 for registration" },
      { id: 3, text: "OEE > 85%" },
    ]);
    setTimeInfo([{ id: 1, text: "Cycle time: 45–75 sec/1000 labels" }]);
    setResponsible([{ id: 1, text: "Printing Supervisor + Quality Inspector" }]);
    setResources([{ id: 1, text: "Nilpeter MO-4 press, AVT Helios vision" }]);
    setRisks([{ id: 1, text: "Substrate elongation → misregistration" }]);
    setCustRequirements([{ id: 1, text: "±0.15 mm registration tolerance" }]);
  };

  const validateBasic = () => {
    if (!processName.trim()) {
      alert("Process Name is required.");
      return false;
    }
    if (processSteps.length === 0) {
      alert("At least one Process Step is required.");
      return false;
    }
    if (outputs.length === 0) {
      alert("At least one Output is required.");
      return false;
    }
    return true;
  };

  const exportToPDF = async (ref, filename) => {
    if (!ref.current) return;
    const canvas = await html2canvas(ref.current, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [canvas.width, canvas.height],
    });
    pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save(filename);
  };

  // ─── React Flow ─────────────────────────────────────────────────────────────
  const generateFlowElements = (isDetailed = false) => {
    const nodes = [];
    const edges = [];

    let y = 50;
    const addLane = (title, items, color, xOffset) => {
      nodes.push({
        id: title,
        type: "default",
        position: { x: xOffset, y: 20 },
        data: { label: <div className={`font-bold text-${color}-300`}>{title}</div> },
        style: { background: "#1a202c", border: `2px solid ${color}`, width: 280 },
      });

      items.forEach((item, i) => {
        const nodeId = `${title}-${item.id}`;
        nodes.push({
          id: nodeId,
          type: "default",
          position: { x: xOffset + 20, y: y + i * 70 },
          data: { label: item.text },
          style: { background: "#2d3748", border: `1px solid ${color}` },
        });
        if (i > 0) {
          edges.push({
            id: `e-${title}-${i}`,
            source: `${title}-${items[i - 1].id}`,
            target: nodeId,
            animated: true,
            style: { stroke: color },
          });
        }
      });
      y += Math.max(100, items.length * 70 + 50);
    };

    addLane("Suppliers", suppliers, "blue", 0);
    addLane("Inputs", inputs, "indigo", 320);
    addLane("Process", processSteps, "teal", 640);
    addLane("Outputs", outputs, "purple", 960);
    addLane("Customers", customers, "pink", 1280);

    if (isDetailed) {
      y += 80;
      addLane("Metrics", metrics, "cyan", 0);
      addLane("Risks", risks, "red", 320);
      addLane("Requirements", custRequirements, "yellow", 640);
    }

    return { nodes, edges };
  };

  // React Flow states – correct destructuring
  const [basicNodes, setBasicNodes, onNodesChangeBasic] = useNodesState([]);
  const [basicEdges, setBasicEdges, onEdgesChangeBasic] = useEdgesState([]);

  const [detailedNodes, setDetailedNodes, onNodesChangeDetailed] = useNodesState([]);
  const [detailedEdges, setDetailedEdges, onEdgesChangeDetailed] = useEdgesState([]);

  React.useEffect(() => {
    if (showBasicChart) {
      const { nodes, edges } = generateFlowElements(false);
      setBasicNodes(Array.isArray(nodes) ? nodes : []);
      setBasicEdges(Array.isArray(edges) ? edges : []);
    }
  }, [showBasicChart, suppliers, inputs, processSteps, outputs, customers]);

  React.useEffect(() => {
    if (showDetailedChart) {
      const { nodes, edges } = generateFlowElements(true);
      setDetailedNodes(Array.isArray(nodes) ? nodes : []);
      setDetailedEdges(Array.isArray(edges) ? edges : []);
    }
  }, [showDetailedChart, suppliers, inputs, processSteps, outputs, customers, metrics, risks, custRequirements]);

  const renderListManager = (
    title,
    items,
    setItems,
    newValue,
    setNewValue,
    color = "teal",
    placeholder = ""
  ) => (
    <div className={`p-5 bg-gray-900/70 rounded-xl border border-${color}-900/60 shadow-md`}>
      <h3 className={`text-xl font-bold text-${color}-300 mb-4`}>{title}</h3>

      <div className="flex gap-3 mb-4">
        <input
          className="flex-1 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-${color}-500"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={placeholder || `Add new ${title.toLowerCase().replace(/s$/, "")}...`}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addItem(setItems, items, newValue, setNewValue);
            }
          }}
        />
        <button
          className={`w-8 text-sm lg:text-lg px-1 py-1 bg-${color}-700 hover:bg-${color}-600 rounded-lg font-medium transition`}
          onClick={() => addItem(setItems, items, newValue, setNewValue)}
        >
          ➕
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd(setItems)}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-3 min-h-[80px]">
            {items.length === 0 ? (
              <p className="text-gray-500 italic text-center py-6">No items yet...</p>
            ) : (
              items.map((item) => (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  text={item.text}

                  onUpdate={(newText) => {
                    // console.log("onUpdate called with newText:", newText, "for id:", id);
                    setItems((prevItems) => {
                      // console.log("Updating list. Current length:", prevItems.length);
                      return prevItems.map((prevItem) =>
                        prevItem.id === id ? { ...prevItem, text: newText.trim() } : prevItem
                      );
                    });
                  }}

                  onDelete={() => {
                    // console.log("onDelete called for id:", id);
                    setItems((prevItems) => {
                      // console.log("Deleting from list. Current length:", prevItems.length);
                      return prevItems.filter((prevItem) => prevItem.id !== id);
                    });
                  }}

                  // onUpdate={(newText) => {
                  //   setItems((prevItems) =>   
                  //     prevItems.map((prevItem) =>
                  //       prevItem.id === id
                  //         ? { ...prevItem, text: newText.trim() }
                  //         : prevItem
                  //     )
                  //   );
                  // }}
            
                  // onDelete={() => {
                  //   setItems((prevItems) =>
                  //     prevItems.filter((prevItem) => prevItem.id !== id)
                  //   );
                  // }}
                  color={color}
                />
              ))
            )}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );

  const [showSIPOCChart, setShowSIPOCChart] = useState(false);
  const SIPOCChart = () => (
    <div className="mt-8 bg-gray-900 [&_li]:text-gray-100 p-6 rounded-xl border border-teal-900/50">
      <h3 className="text-cyan-300 text-xl mb-6 text-center">SIPOC Diagram</h3>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
        {/* Suppliers */}
        <div className="bg-gradient-to-b from-blue-950 to-gray-950 p-4 rounded-lg border border-blue-800/60">
          <h4 className="text-blue-300 font-bold mb-3 text-center">SUPPLIERS</h4>
          {suppliers.length === 0 ? (
            <p className="text-gray-500 text-center text-xs italic">— not defined —</p>
          ) : (
            <ul className="space-y-2">
              {suppliers.map((item) => (
                <li key={item.id} className="bg-blue-950/40 p-2 rounded">
                  {item.text}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Inputs */}
        <div className="bg-gradient-to-b from-indigo-950 to-gray-950 p-4 rounded-lg border border-indigo-800/60">
          <h4 className="text-indigo-300 font-bold mb-3 text-center">INPUTS</h4>
          {inputs.length === 0 ? (
            <p className="text-gray-500 text-center text-xs italic">— not defined —</p>
          ) : (
            <ul className="space-y-2">
              {inputs.map((item) => (
                <li key={item.id} className="bg-indigo-950/40 p-2 rounded">
                  {item.text}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Process */}
        <div className="bg-gradient-to-b from-teal-950 to-gray-950 p-4 rounded-lg border border-teal-800/60 row-span-1 md:row-span-3 flex flex-col">
          <h4 className="text-teal-300 font-bold mb-3 text-center">PROCESS</h4>
          {processSteps.length === 0 ? (
            <p className="text-gray-500 text-center text-xs italic">— not defined —</p>
          ) : (
            <ol className="list-decimal list-inside space-y-2 text-left">
              {processSteps.map((step) => (
                <li key={step.id} className="bg-teal-950/40 p-2 rounded">
                  {step.text}
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Outputs */}
        <div className="bg-gradient-to-b from-purple-950 to-gray-950 p-4 rounded-lg border border-purple-800/60">
          <h4 className="text-purple-300 font-bold mb-3 text-center">OUTPUTS</h4>
          {outputs.length === 0 ? (
            <p className="text-gray-500 text-center text-xs italic">— not defined —</p>
          ) : (
            <ul className="space-y-2">
              {outputs.map((item) => (
                <li key={item.id} className="bg-purple-950/40 p-2 rounded">
                  {item.text}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Customers */}
        <div className="bg-gradient-to-b from-pink-950 to-gray-950 p-4 rounded-lg border border-pink-800/60">
          <h4 className="text-pink-300 font-bold mb-3 text-center">CUSTOMERS</h4>
          {customers.length === 0 ? (
            <p className="text-gray-500 text-center text-xs italic">— not defined —</p>
          ) : (
            <ul className="space-y-2">
              {customers.map((item) => (
                <li key={item.id} className="bg-pink-950/40 p-2 rounded">
                  {item.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-black text-gray-100 font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500 px-4 py-3 flex justify-between items-center">
        <div>
          <div className="text-xl font-bold text-yellow-300">🗺️ Think5Whys Sigma Studio</div>
          <div className="text-xs text-gray-100">SIPOC Playground</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm"
            >
              🔐
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm">{userName}</div>
              <button onClick={handleLogout} className="p-2 rounded-md bg-red-600">
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">
                🔐
              </Link>
            </div>
          )}
        </div>
      )}

      <main className="flex-1 p-2 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 place-items-center mb-2">
          <button
            onClick={populateExampleData}
            className="min-h-[40px] px-2 py-2 text-sm lg:text-base bg-green-700 hover:bg-green-600 rounded-lg font-semibold"
          >
            Load Realistic Label Printing Example
          </button>
          <button
            onClick={() => setShowWhyUsed(!showWhyUsed)}
            className="min-h-[40px] px-2 py-2 text-sm lg:text-base bg-green-700 hover:bg-green-600 rounded-lg font-semibold"
          >
            Why Use SIPOC + Advanced?
          </button>
        </div>

        {showWhyUsed && (
          <div className="mb-10 p-6 bg-gray-900 rounded-xl border border-teal-800 text-center">
            <p className="text-lg text-cyan-200">
              SIPOC = Scope clarity. Advanced fields help connect to Six Sigma metrics, risk management & customer CTQs.
            </p>
          </div>
        )}

        {/* ─── Basic Fields ─── */}
        <div className="space-y-6 mb-12 max-w-4xl mx-auto">
          <div>
            <label className="block mb-1 text-cyan-200 font-medium">Process Name</label>
            <input
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              value={processName}
              onChange={(e) => setProcessName(e.target.value)}
              placeholder="e.g. Self-Adhesive Label Printing Process"
            />
          </div>

          <div>
            <label className="block mb-1 text-cyan-200 font-medium">Title / Scope</label>
            <input
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder="e.g. Multi-color Printing to Finished Roll Packing"
            />
            <button
              className="text-xs lg:text-sm w-32 lg:w-48 mt-2 px-4 py-1.5 bg-blue-700 hover:bg-blue-600 rounded font-medium"
              onClick={() => setShowTDetails(!showTDetails)}
            >
              {showTDetails ? "Hide" : "Show"} Description
            </button>
            {showTDetails && (
              <textarea
                className="w-full mt-3 p-3 bg-gray-900 border border-gray-700 rounded text-white h-32 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={taskDetails}
                onChange={(e) => setTaskDetails(e.target.value)}
                placeholder="Brief description of process scope and purpose..."
              />
            )}
          </div>

          <div>
            <label className="block mb-1 text-cyan-200 font-medium">Remarks / Key Observations</label>
            <textarea
              className="w-full p-3 bg-gray-900 border border-gray-700 rounded text-white h-24 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              value={finalRemarks}
              onChange={(e) => setFinalRemarks(e.target.value)}
              placeholder="Any important notes, risks, next steps, or lessons learned..."
            />
          </div>
        </div>

        {/* SIPOC Sections */}
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          {renderListManager("Suppliers", suppliers, setSuppliers, newSupplier, setNewSupplier, "blue", "e.g. Avery Dennison")}
          {renderListManager("Inputs", inputs, setInputs, newInput, setNewInput, "indigo", "e.g. 80gsm semi-gloss laminate")}
          {renderListManager("Process Steps", processSteps, setProcessSteps, newProcessStep, setNewProcessStep, "teal", "e.g. Multi-color printing")}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {renderListManager("Outputs", outputs, setOutputs, newOutput, setNewOutput, "purple", "e.g. Labeled rolls + waste")}
          {renderListManager("Customers", customers, setCustomers, newCustomer, setNewCustomer, "pink", "e.g. Nestlé India")}
        </div>

        {/* Advanced Toggle */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="px-8 py-4 bg-gradient-to-r from-teal-700 to-cyan-700 hover:from-teal-600 hover:to-cyan-600 rounded-xl font-bold text-lg shadow-lg"
          >
            {showAdvanced ? "Hide" : "Add More Details →"} Metrics, Risks, Requirements...
          </button>
        </div>

        {showAdvanced && (
          <div className="space-y-10 animate-fade-in">
            <div className="grid lg:grid-cols-3 gap-6">
              {renderListManager("Process Metrics / KPIs", metrics, setMetrics, newMetric, setNewMetric, "cyan", "e.g. Cpk ≥ 1.33")}
              {renderListManager("Risks / Failure Modes", risks, setRisks, newRisk, setNewRisk, "red", "e.g. Ink drying too fast")}
              {renderListManager("Customer Requirements / CTQs", custRequirements, setCustRequirements, newReq, setNewReq, "yellow", "e.g. ±0.2 mm reg.")}
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {renderListManager("Time / Cycle", timeInfo, setTimeInfo, newTime, setNewTime, "emerald", "e.g. 60 s / 1000 labels")}
              {renderListManager("Responsible Parties", responsible, setResponsible, newResp, setNewResp, "orange", "e.g. Press Operator")}
              {renderListManager("Resources / Tools", resources, setResources, newResource, setNewResource, "violet", "e.g. Nilpeter MO-Line")}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 place-items-center mt-4">
          <button
            className="min-h-[40px] px-2 py-2 text-sm lg:text-base font-bold rounded-xl bg-indigo-700 hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setShowSIPOCChart(!showSIPOCChart)}
          >
            {showSIPOCChart ? "Hide Basic SIPOC Chart" : "View Basic SIPOC Chart"}
          </button>

          <button
            className="min-h-[40px] px-2 py-2 text-sm lg:text-base font-bold rounded-xl bg-purple-700 hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              if (validateBasic()) setShowBasicChart(!showBasicChart);
            }}
          >
            {showBasicChart ? "Hide Zoomable Basic SIPOC Chart" : "View Zoomable Basic SIPOC Chart"}
          </button>

          <button
            className="min-h-[40px] px-2 py-2 text-sm lg:text-base font-bold rounded-xl bg-indigo-700 hover:bg-indigo-600 transition-colors disabled:bg-gray-200 disabled:text-black disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              if (validateBasic()) setShowDetailedChart(!showDetailedChart);
            }}
            disabled={!showAdvanced}
          >
            {showDetailedChart
              ? "Hide Zoomable Detailed SIPOC (with Metrics/Risks)"
              : "Show Detailed SIPOC (with Metrics/Risks)"}
          </button>

          {showSIPOCChart && (
            <button
              className="min-h-[40px] px-2 py-2 text-sm lg:text-base font-bold rounded-xl bg-teal-700 hover:bg-teal-600 transition-colors"
              onClick={() => exportToPDF(basicRef, "Basic-SIPOC.pdf")}
            >
              Export Simple to PDF
            </button>
          )}

          {showBasicChart && (
            <button
              className="min-h-[40px] px-2 py-2 text-sm lg:text-base font-bold rounded-xl bg-teal-700 hover:bg-teal-600 transition-colors"
              onClick={() => exportToPDF(basicRef, "Basic-SIPOC.pdf")}
            >
              Export Basic to PDF
            </button>
          )}

          {showDetailedChart && (
            <button
              className="min-h-[40px] px-2 py-2 text-sm lg:text-base font-bold rounded-xl bg-teal-700 hover:bg-teal-600 transition-colors"
              onClick={() => exportToPDF(detailedRef, "Detailed-SIPOC.pdf")}
            >
              Export Detailed to PDF
            </button>
          )}
        </div>

        {showSIPOCChart && <SIPOCChart />}


        {/* Charts */}
        {showBasicChart && (
          <div ref={basicRef} className="mt-4 [&_p]:text-white bg-gray-950 p-8 rounded-2xl border border-purple-800/50 min-h-[600px]">
            <h2 className="text-2xl font-bold text-center text-purple-300 mb-6">Basic SIPOC Flow Diagram</h2>
            <div style={{ height: "600px" }}>
              <ReactFlowProvider>
                <ReactFlow
                  nodes={basicNodes}
                  edges={basicEdges}
                  onNodesChange={onNodesChangeBasic}
                  onEdgesChange={onEdgesChangeBasic}
                  fitView
                  minZoom={0.4}
                  maxZoom={2}
                  theme="dark"
                  style={{ background: "#0f172a" }}
                >
                  <Background variant="dots" gap={24} size={1.5} color="#475569" />
                  <Controls />
                  <MiniMap pannable zoomable nodeColor="#64748b" nodeStrokeColor="#94a3b8" />
                </ReactFlow>
              </ReactFlowProvider>
            </div>
          </div>
        )}

        {showDetailedChart && (
          <div ref={detailedRef} className="mt-12 bg-gray-950 p-8 rounded-2xl border border-indigo-800/50 min-h-[800px]">
            <h2 className="text-2xl font-bold text-center text-indigo-300 mb-6">Detailed SIPOC + Metrics & Risks</h2>
            <div style={{ height: "800px" }}>
              <ReactFlowProvider>
                <ReactFlow
                  nodes={detailedNodes}
                  edges={detailedEdges}
                  onNodesChange={onNodesChangeDetailed}
                  onEdgesChange={onEdgesChangeDetailed}
                  fitView
                  minZoom={0.3}
                  maxZoom={2.5}
                  theme="dark"
                  style={{ background: "#0f172a" }}
                >
                  <Background variant="dots" gap={40} size={1} color="#475569" />
                  <Controls />
                  <MiniMap pannable zoomable nodeColor="#64748b" nodeStrokeColor="#94a3b8" />
                </ReactFlow>
              </ReactFlowProvider>
            </div>
          </div>
        )}
      </main>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-auto">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Small draggable item component
const SortableItem = ({ id, text, onUpdate, onDelete, color = "gray" }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(text);

  const handleSave = () => {
    if (editText.trim()) onUpdate(editText);
    setEditing(false);
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-3 p-3 bg-gray-800 rounded-lg border-l-4 border-${color}-600 hover:bg-gray-700 transition group cursor-grab active:cursor-grabbing`}
    >
      {editing ? (
        <>
          <input
            className="flex-1 bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
              if (e.key === "Escape") {
                setEditText(text);
                setEditing(false);
              }
            }}
            autoFocus
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            className="w-8 text-sm lg:text-lg px-1 py-1 text-green-400 hover:text-green-300"
          >
            💾
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 text-gray-100">{text}</span>
          <button
            onClick={(e) => {
              e.stopPropagation(); 
              // console.log("Edit button clicked for id:", id);         
              setEditing(true);
            }}
            className="w-8 text-sm lg:text-lg px-1 py-1 text-blue-400 hover:text-blue-400 leading-none"
          >
            ✏️ 
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();  
              // console.log("Delete button clicked for id:", id);       
              onDelete();
            }}
            className="w-8 text-sm lg:text-lg px-1 py-1 text-red-400 hover:text-red-400 leading-none"
          >
            🗑️ 
          </button>
        </>
      )}
    </li>
  );
};

export default SigmaStudioSIPOC;